import type { ReleaseThemeId } from '../releaseThemes';

import { motionArtworkWatermark, type PlacedWatermark } from './watermark';

/**
 * The Apple Music motion artwork (design `asset-redesign/w Motion artwork - new`, owner answers
 * 2026-10-08): the cover itself in motion. Frame 0 is the cover with no change, no theme frame and
 * no text. The release theme gives only a motion signature (`MOTION_SIGNATURES`). The square file
 * scales at most 1.03 and never pans; the tall file shows the whole cover in the iPhone safe band
 * over a blurred copy of the cover (`MOTION_TALL_LAYOUT`). The loop is the whole bars of the song
 * nearest to 20 s (`motionArtworkLoop`), and every curve has the loop as its period, so the last
 * frame runs into frame 0 with no jump.
 *
 * Two readers use one table: the web Studio preview (`motionArtworkPose`, CSS transforms) and the
 * ffmpeg renderer of music24-api (`motionArtworkExpressions`, the same curves as ffmpeg
 * expressions). A test evaluates both on the same frames. Pure.
 */

/** The frame rate of the motion artwork files. */
export const MOTION_ARTWORK_FRAME_RATE = 30;
/** The length the loop aims at, and its bounds (Apple: 8 to 35 s; the spec 15 to 30 s). */
export const MOTION_ARTWORK_TARGET_SEC = 20;
export const MOTION_ARTWORK_MIN_SEC = 15;
export const MOTION_ARTWORK_MAX_SEC = 30;
/** With no beat grid: 20 s in 10 phrases of 2 s (the pulse and the glow keep a tempo). */
const FALLBACK_BARS = 10;

/** The light of a signature, drawn in screen mode over the moving cover. */
export type MotionArtworkLight = 'none' | 'bloom' | 'sweep' | 'sun' | 'glow';

/** The motion signature of a release theme (the table of page w). */
export interface MotionSignature {
  /** The UI words of page w ("slow push", "bar pulse"). */
  label: string;
  /** The peak extra scale of the one slow push or breath per loop (0.03 = 1.03). */
  push: number;
  /** The extra scale of the pulse on the first beat of each bar (Hype); 0: none. */
  barPulse: number;
  /** The sway of a print in a hand: the peak turn in degrees (Handmade); 0: none. */
  swayDeg: number;
  /** The push moves in steps of this many frames (Retro); 1: smooth. */
  stepFrames: number;
  light: MotionArtworkLight;
  /** The peak strength of the light (0..1 of a screen layer). */
  lightMax: number;
}

export const MOTION_SIGNATURES: Readonly<Record<ReleaseThemeId, MotionSignature>> = {
  clean: { label: 'slow push', push: 0.03, barPulse: 0, swayDeg: 0, stepFrames: 1, light: 'none', lightMax: 0 },
  hype: { label: 'bar pulse', push: 0, barPulse: 0.015, swayDeg: 0, stepFrames: 1, light: 'none', lightMax: 0 },
  dreamy: {
    label: 'breath and bloom',
    push: 0.02,
    barPulse: 0,
    swayDeg: 0,
    stepFrames: 1,
    light: 'bloom',
    lightMax: 0.38,
  },
  dark: { label: 'light sweep', push: 0.02, barPulse: 0, swayDeg: 0, stepFrames: 1, light: 'sweep', lightMax: 0.22 },
  warm: { label: 'late sun', push: 0.03, barPulse: 0, swayDeg: 0, stepFrames: 1, light: 'sun', lightMax: 0.55 },
  retro: { label: 'stepped push', push: 0.03, barPulse: 0, swayDeg: 0, stepFrames: 2, light: 'none', lightMax: 0 },
  neon: { label: 'glow swell', push: 0.02, barPulse: 0, swayDeg: 0, stepFrames: 1, light: 'glow', lightMax: 0.42 },
  handmade: { label: 'hand-held sway', push: 0, barPulse: 0, swayDeg: 0.5, stepFrames: 1, light: 'none', lightMax: 0 },
};

/** The Hype pulse: up in 2 frames, back to rest in 0.55 s (an ease-out). */
export const MOTION_PULSE_ATTACK_SEC = 2 / MOTION_ARTWORK_FRAME_RATE;
export const MOTION_PULSE_DECAY_SEC = 0.55;
/**
 * The sway scale: 1 + 0.03 × the share of the peak turn, so the corners stay filled (a 0.5° turn
 * needs 1.009) and the square scale stays at 1.03 or less (design w).
 */
export const MOTION_SWAY_SCALE = 0.03;
/** The Noir sweep crosses the cover from 15 % to 65 % of the loop (Swamps: 3.1 to 13.3 s). */
export const MOTION_SWEEP_WINDOW = { from: 0.15, to: 0.65 } as const;
/** The Neon glow between swells (page w: .12 to .42 every second bar). */
export const MOTION_GLOW_FLOOR = 0.12;

/**
 * The tall file (2048 x 2732, the iPhone album page): the whole cover, 78 % of the width, in the
 * safe band from 125 / 1334 of the height (under the status bar, over the title and the buttons);
 * the rest is the same cover, cover-fit, blurred (90 px at 2048) and darker (0.82, darker still
 * toward the bottom), drifting 2 % on a closed path with a zoom to 1.05. The band is an estimate
 * from the iOS app (owner default 2026-10-08).
 */
export const MOTION_TALL_LAYOUT = {
  coverWidthShare: 0.78,
  coverTopShare: 125 / 1334,
  groundBlurShare: 90 / 2048,
  groundBrightness: 0.82,
  /** The ground darkens by this much more from 55 % of the height to the bottom edge. */
  groundBottomDarken: 0.22,
  groundDrift: 0.02,
  groundZoom: 0.05,
} as const;

/** The loop: whole bars, their count and the frames. */
export interface MotionArtworkLoop {
  bars: number;
  loopSec: number;
  loopFrames: number;
}

/**
 * The loop of a song with a bar of `barSec` (`songBarSec`): the whole bars nearest to 20 s inside
 * 15 to 30 s (Swamps: 11 bars, 20.43 s). With no beat grid: 20 s in 10 phrases. `overrideSec`
 * (the Studio length 15, 20 or 30 s) keeps that length with the bar count nearest to it. Pure.
 */
export function motionArtworkLoop(barSec: number | null, overrideSec: number | null = null): MotionArtworkLoop {
  const target = overrideSec ?? MOTION_ARTWORK_TARGET_SEC;
  const frames = (sec: number): number => Math.round(sec * MOTION_ARTWORK_FRAME_RATE);
  if (barSec === null || !Number.isFinite(barSec) || barSec <= 0) {
    return { bars: FALLBACK_BARS, loopSec: target, loopFrames: frames(target) };
  }
  if (overrideSec !== null) {
    const bars = Math.max(1, Math.round(overrideSec / barSec));
    return { bars, loopSec: overrideSec, loopFrames: frames(overrideSec) };
  }
  let bars = Math.max(1, Math.round(target / barSec));
  while (bars * barSec > MOTION_ARTWORK_MAX_SEC && bars > 1) {
    bars -= 1;
  }
  while (bars * barSec < MOTION_ARTWORK_MIN_SEC) {
    bars += 1;
  }
  const loopFrames = frames(bars * barSec);
  return { bars, loopSec: Math.round((loopFrames / MOTION_ARTWORK_FRAME_RATE) * 1000) / 1000, loopFrames };
}

/** The pose of one frame: the cover scale and turn about its centre, and the light. */
export interface MotionArtworkPose {
  scale: number;
  turnDeg: number;
  /** The strength of the light layer (0..1); 0 at frame 0. */
  light: number;
  /** The Noir band: its place across the cover (0 = the left edge, 1 = the right edge); null: none. */
  sweepAt: number | null;
}

const cosine01 = (share: number): number => (1 - Math.cos(2 * Math.PI * share)) / 2;

/** The Neon swell envelope: up over the first bar, down over the last, 1 between (a sine ease). */
function glowEnvelope(frame: number, loopFrames: number, barFrames: number): number {
  const edge = Math.min(1, frame / barFrames, (loopFrames - frame) / barFrames);
  return (1 - Math.cos(Math.PI * Math.max(0, edge))) / 2;
}

/**
 * The pose of the motion artwork of a theme at `frame` of a loop of `loopFrames` frames and `bars`
 * bars. Frame 0 (and frame `loopFrames`) is at rest: scale 1, no turn, no light. Pure.
 */
export function motionArtworkPose(
  themeId: ReleaseThemeId,
  frame: number,
  loop: Pick<MotionArtworkLoop, 'bars' | 'loopFrames'>
): MotionArtworkPose {
  const signature = MOTION_SIGNATURES[themeId];
  const stepped = signature.stepFrames > 1 ? signature.stepFrames * Math.floor(frame / signature.stepFrames) : frame;
  const share = stepped / loop.loopFrames;
  const barFrames = loop.loopFrames / Math.max(1, loop.bars);
  let scale = 1 + signature.push * cosine01(share);
  let turnDeg = 0;
  if (signature.barPulse > 0) {
    const sinceBar = (frame % barFrames) / MOTION_ARTWORK_FRAME_RATE;
    scale += signature.barPulse * pulseShape(sinceBar);
  }
  if (signature.swayDeg > 0) {
    const sway = Math.sin(2 * Math.PI * share);
    turnDeg = signature.swayDeg * sway;
    scale += MOTION_SWAY_SCALE * Math.abs(sway);
  }
  let light = 0;
  let sweepAt: number | null = null;
  switch (signature.light) {
    case 'bloom':
    case 'sun':
      light = signature.lightMax * cosine01(share);
      break;
    case 'glow':
      light =
        glowEnvelope(frame, loop.loopFrames, barFrames) *
        (MOTION_GLOW_FLOOR + (signature.lightMax - MOTION_GLOW_FLOOR) * cosine01(frame / (2 * barFrames)));
      break;
    case 'sweep': {
      const at = (share - MOTION_SWEEP_WINDOW.from) / (MOTION_SWEEP_WINDOW.to - MOTION_SWEEP_WINDOW.from);
      if (at > 0 && at < 1) {
        light = signature.lightMax;
        sweepAt = -0.35 + 1.7 * ((1 - Math.cos(Math.PI * at)) / 2);
      }
      break;
    }
    case 'none':
      break;
  }
  return { scale, turnDeg, light, sweepAt };
}

/** The Hype pulse at `sinceBarSec` after a bar start: 0 at the start, 1 after 2 frames, 0 again 0.55 s later. */
function pulseShape(sinceBarSec: number): number {
  if (sinceBarSec < MOTION_PULSE_ATTACK_SEC) {
    return sinceBarSec / MOTION_PULSE_ATTACK_SEC;
  }
  const decay = (sinceBarSec - MOTION_PULSE_ATTACK_SEC) / MOTION_PULSE_DECAY_SEC;
  return decay < 1 ? (1 - decay) ** 3 : 0;
}

/**
 * The closed drift path of the blurred ground of the tall file at `frame`: a zoom to 1.05 and a
 * move of 2 % on an ellipse, one turn per loop; at rest on frame 0. Pure.
 */
export function motionGroundPose(frame: number, loopFrames: number): { scale: number; dx: number; dy: number } {
  const share = frame / loopFrames;
  return {
    scale: 1 + MOTION_TALL_LAYOUT.groundZoom * cosine01(share),
    dx: MOTION_TALL_LAYOUT.groundDrift * Math.sin(2 * Math.PI * share),
    dy: MOTION_TALL_LAYOUT.groundDrift * 0.6 * Math.sin(4 * Math.PI * share),
  };
}

/** The whole cover in the tall file: its square box in pixels of a `width` x `height` frame. Pure. */
export function motionTallCoverBox(width: number, height: number): { x: number; y: number; size: number } {
  const size = Math.round((width * MOTION_TALL_LAYOUT.coverWidthShare) / 2) * 2;
  return { x: Math.round((width - size) / 2), y: Math.round(height * MOTION_TALL_LAYOUT.coverTopShare), size };
}

/** The ffmpeg expressions of a pose (`frame` is the name of the frame variable of the filter). */
export interface MotionArtworkExpressions {
  scale: string;
  /** The turn in radians. */
  turnRad: string;
  /** The light strength; '0' with no light. */
  light: string;
  /** The Noir band place; null with no sweep. */
  sweepAt: string | null;
}

const num = (value: number): string => {
  const text = Number(value.toFixed(6)).toString();
  return value < 0 ? `(${text})` : text;
};

/**
 * The curves of `motionArtworkPose` as ffmpeg expressions of the frame variable `frame` (`on` in
 * `perspective`, `N` in `geq`). They use only `sin`, `cos`, `abs`, `floor`, `pow`, `mod`, `if`,
 * `lt`, `gt`, `min`, `max` and `PI`, and give the same numbers as the pose. Pure.
 */
export function motionArtworkExpressions(
  themeId: ReleaseThemeId,
  loop: Pick<MotionArtworkLoop, 'bars' | 'loopFrames'>,
  frame: string
): MotionArtworkExpressions {
  const signature = MOTION_SIGNATURES[themeId];
  const loopFrames = num(loop.loopFrames);
  const barFrames = num(loop.loopFrames / Math.max(1, loop.bars));
  const stepped =
    signature.stepFrames > 1 ? `(${signature.stepFrames}*floor(${frame}/${signature.stepFrames}))` : frame;
  const share = `(${stepped}/${loopFrames})`;
  const cosine = (of: string): string => `((1-cos(2*PI*${of}))/2)`;
  const scaleParts = ['1'];
  if (signature.push > 0) {
    scaleParts.push(`${num(signature.push)}*${cosine(share)}`);
  }
  if (signature.barPulse > 0) {
    const since = `(mod(${frame},${barFrames})/${MOTION_ARTWORK_FRAME_RATE})`;
    const attack = num(MOTION_PULSE_ATTACK_SEC);
    const decay = `((${since}-${attack})/${num(MOTION_PULSE_DECAY_SEC)})`;
    scaleParts.push(
      `${num(signature.barPulse)}*if(lt(${since},${attack}),${since}/${attack},if(lt(${decay},1),pow(1-${decay},3),0))`
    );
  }
  let turnRad = '0';
  if (signature.swayDeg > 0) {
    const sway = `sin(2*PI*${share})`;
    turnRad = `(${num((signature.swayDeg * Math.PI) / 180)}*${sway})`;
    scaleParts.push(`${num(MOTION_SWAY_SCALE)}*abs(${sway})`);
  }
  let light = '0';
  let sweepAt: string | null = null;
  switch (signature.light) {
    case 'bloom':
    case 'sun':
      light = `(${num(signature.lightMax)}*${cosine(share)})`;
      break;
    case 'glow': {
      const edge = `max(0,min(1,min(${frame}/${barFrames},(${loopFrames}-${frame})/${barFrames})))`;
      const envelope = `((1-cos(PI*${edge}))/2)`;
      const swell = cosine(`(${frame}/(2*${barFrames}))`);
      light = `(${envelope}*(${num(MOTION_GLOW_FLOOR)}+${num(signature.lightMax - MOTION_GLOW_FLOOR)}*${swell}))`;
      break;
    }
    case 'sweep': {
      const at = `((${share}-${num(MOTION_SWEEP_WINDOW.from)})/${num(MOTION_SWEEP_WINDOW.to - MOTION_SWEEP_WINDOW.from)})`;
      light = `if(gt(${at},0)*lt(${at},1),${num(signature.lightMax)},0)`;
      sweepAt = `((-0.35)+1.7*((1-cos(PI*${at}))/2))`;
      break;
    }
    case 'none':
      break;
  }
  return { scale: `(${scaleParts.join('+')})`, turnRad, light, sweepAt };
}

/** The ffmpeg expressions of `motionGroundPose`. Pure. */
export function motionGroundExpressions(loopFrames: number, frame: string): { scale: string; dx: string; dy: string } {
  const share = `(${frame}/${num(loopFrames)})`;
  return {
    scale: `(1+${num(MOTION_TALL_LAYOUT.groundZoom)}*((1-cos(2*PI*${share}))/2))`,
    dx: `(${num(MOTION_TALL_LAYOUT.groundDrift)}*sin(2*PI*${share}))`,
    dy: `(${num(MOTION_TALL_LAYOUT.groundDrift * 0.6)}*sin(4*PI*${share}))`,
  };
}

/**
 * The size note of Studio step 3 (design w, "Cover size"): Apple asks for 3840 x 3840, so a smaller
 * cover is scaled up and looks soft. Null when the cover is large enough or its size is unknown. Pure.
 */
export function motionArtworkCoverNote(coverPx: number | null): { coverPx: number; targetPx: number } | null {
  const targetPx = 3840;
  if (coverPx === null || !Number.isFinite(coverPx) || coverPx <= 0 || coverPx >= targetPx) {
    return null;
  }
  return { coverPx: Math.round(coverPx), targetPx };
}

/**
 * The Free watermark lockup of a motion artwork file (owner rule 2026-10-08: every frame of every
 * Free and trial file carries it; paid files have none, and only there frame 0 is the exact cover).
 * Design w0: a glass stack, L 185 on the square file (centred, foot at y 3610 of 3840); on the tall
 * file L 99, centred on the cover, its foot 24 px over the foot of the cover band (inside the iPhone
 * safe band, clear of the album title and the buttons). A half-size preview scales with the file. Pure.
 */
export function motionWatermark(frame: { width: number; height: number }): PlacedWatermark {
  const tall = frame.height > frame.width;
  return motionArtworkWatermark(frame, tall ? motionTallCoverBox(frame.width, frame.height) : null);
}
